import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Divider, Form, Input, InputNumber, Select, Switch } from 'antd';
import { DatabaseFilled, GlobalOutlined } from '@ant-design/icons';
import { SelectableList } from './SelectableList';
import cx from 'classnames';
import { appConfig } from '../../../../settings';
import { IPostPackage, ISelectList } from '../../../../interfaces';
import { useCompaniesChildrenSelect, useCompaniesSelect, useProfile } from '../../../../hooks';
import {
    apiMessagesGetFormRules,
    getCatalogsDataUnitTypeSelect,
    getCompaniesChildrenSelect,
    getCompaniesSelect,
    getNetworksSelect,
    getOperatorsSelect,
} from '../../../../middleware';
import { IParams } from '../../../../instruments';
import { tB, tCustom, tF, tT, tV } from '../../../../translate';
import { FormSelect, LimitUnitSelector } from '../../../Forms';
import TariffsStyles from '../../m_styles.less';

interface ICreatePackageForm {
    packageType: 'local' | 'roaming' | 'extra';
    onBack: () => void;
    onSave: (data: IPostPackage) => void;
    initialValues?: IPostPackage;
    tariffCompanyId?: string;
}

export const CreatePackageForm: React.FC<ICreatePackageForm> = observer(
    ({ packageType, onBack, onSave, initialValues, tariffCompanyId }: ICreatePackageForm) => {
        const [form] = Form.useForm();
        const { currency } = useProfile();
        const currencyObj = appConfig.currency.find((currencyObj) => currencyObj.abbreviation === currency);
        const [selectedOperators, setSelectedOperators] = useState<string[]>(
            initialValues?.operatorIds.map((value) => value.toString()) || [],
        );
        const [selectedNetworks, setSelectedNetworks] = useState<string[]>(
            initialValues?.networkIds.map((value) => value.toString()) || [],
        );
        const [dataToggle, setDataToggle] = useState<boolean>(!!initialValues?.dataVolume);
        const [callsToggle, setCallsToggle] = useState<boolean>(!!initialValues?.voiceVolume);
        const [smsToggle, setSmsToggle] = useState<boolean>(!!initialValues?.smsVolume);

        const companies = useCompaniesSelect();
        const companySelect = companies.map((company: any) => ({
            label: company.name,
            value: +company.id,
            id: company.id,
        })) as never as ISelectList;
        const [currentCompany, setCurrentCompany] = useState<string>();

        const childrenCompanies = useCompaniesChildrenSelect();
        const childrenCompanySelect = childrenCompanies.map((company: any) => ({
            label: company.name,
            value: +company.id,
            id: company.id,
        })) as never as ISelectList;
        const [currentChildrenCompanies, setChildrenCompanies] = useState<string[]>(
            initialValues?.companyIds?.map((value: number) => value.toString()) || [],
        );

        const convertVoiceToSeconds = (value: number, unit: string): number => {
            switch (unit) {
                case 'hours':
                    return value * 3600;
                case 'min':
                    return value * 60;
                case 'seconds':
                    return value;
                default:
                    return value;
            }
        };

        const handleSave = async () => {
            try {
                if (!dataToggle && !callsToggle && !smsToggle) {
                    form.setFields([
                        {
                            name: 'allowanceValidation',
                            errors: [tV('At least one allowance must be enabled!')],
                        },
                    ]);
                    return;
                }

                const values = await form.validateFields();

                const voiceVolumeInSeconds = callsToggle
                    ? convertVoiceToSeconds(values.callsAllowance, values.callsAllowanceUnits)
                    : 0;
                const smsVolume = smsToggle ? values.smsAllowance : 0;

                const packageData: IPostPackage = {
                    companyId: form.getFieldValue('company_id'),
                    companyIds:
                        packageType === 'extra' ? currentChildrenCompanies.map((id) => parseInt(id)) : undefined,
                    dataVolume: values.dataAllowance,
                    dataVolumeUnit: values.dataAllowanceUnits,
                    name: values.packageName,
                    networkIds: selectedNetworks.map((id) => parseInt(id)),
                    operatorIds: selectedOperators.map((id) => parseInt(id)),
                    packageType,
                    planType: values.planType,
                    priceCents: values.price * 100,
                    productId: values.productId,
                    smsVolume: smsVolume,
                    voiceVolume: voiceVolumeInSeconds,
                };
                onSave(packageData);
            } catch (error) {
                console.error('Validation failed:', error);
            }
        };

        const getInitialData = () => {
            return {
                callsAllowance: callsToggle ? (initialValues?.voiceVolume || 0) / 3600 : 1,
                companyId: initialValues?.companyId || '',
                companyIds: initialValues?.companyIds || [],
                dataAllowance: dataToggle ? initialValues?.dataVolume || 0 : 1,
                dataAllowanceUnits: initialValues?.dataVolumeUnit || 'mb',
                networkIds: initialValues?.networkIds || [],
                operatorIds: initialValues?.operatorIds || [],
                packageName: initialValues?.name,
                planType: initialValues?.planType,
                priceCents: packageType === 'extra' ? initialValues?.priceCents : undefined,
                productId: initialValues?.productId,
                smsAllowance: smsToggle ? initialValues?.smsVolume || 0 : 1,
            };
        };

        const useTime = (): ISelectList => [
            { id: 'seconds', name: 'seconds', value: 'seconds' },
            { id: 'min', name: 'min', value: 'min' },
            { id: 'hours', name: 'hours', value: 'hours' },
        ];

        useEffect(() => {
            if (tariffCompanyId) {
                form.setFieldValue('company_id', tariffCompanyId);
            } else {
                getCompaniesSelect(true);
                form.setFieldValue('company_id', initialValues?.companyId);
                setCurrentCompany(initialValues?.companyId);
            }
        }, [tariffCompanyId]);

        useEffect(() => {
            if (currentCompany && packageType === 'extra') {
                getCompaniesChildrenSelect(currentCompany, true);
            }
        }, [currentCompany, packageType]);

        useEffect(() => {
            getCatalogsDataUnitTypeSelect();
        }, []);

        return (
            <div>
                <div className={TariffsStyles.formContainer}>
                    <div className={TariffsStyles.formHeader}>
                        <div>
                            <p className={TariffsStyles.formDescription}>
                                {tCustom('Configure a type package', {
                                    type: packageType,
                                })}
                            </p>
                        </div>
                    </div>

                    <Divider className={TariffsStyles.formDivider} />

                    <Form
                        form={form}
                        layout="vertical"
                        className={cx('formStyle', TariffsStyles.width100)}
                        initialValues={{
                            callsAllowanceUnits: 'hours',
                            packageType: packageType,
                            ...getInitialData(),
                        }}
                    >
                        <Form.Item
                            label={tF('Package Name')}
                            name="packageName"
                            rules={[{ required: true, message: 'Please enter package name!' }]}
                        >
                            <Input
                                placeholder={`e.g. ${
                                    packageType === 'local'
                                        ? 'UK Standard Local'
                                        : packageType === 'roaming'
                                        ? 'EU Roaming Plus'
                                        : 'Extra Data Package'
                                }`}
                            />
                        </Form.Item>
                        <Form.Item
                            label={tF('Owner')}
                            name="company_id"
                            rules={[{ required: true, message: tV('Please select owner') }]}
                        >
                            <Select
                                showSearch
                                filterOption={(input, option: any) =>
                                    option?.label.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
                                    option?.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                                placeholder={tF('Choose a company')}
                                className={TariffsStyles.width100}
                                options={companySelect}
                                value={tariffCompanyId || currentCompany}
                                disabled={!!tariffCompanyId}
                                onChange={(value) => {
                                    setCurrentCompany(value);
                                    form.setFieldValue('company_id', value);
                                    form.setFieldValue('companyIds', []);
                                    form.setFieldValue('operatorIds', []);
                                    form.setFieldValue('networkIds', []);
                                    setSelectedOperators([]);
                                    setSelectedNetworks([]);
                                    setChildrenCompanies([]);
                                }}
                            />
                        </Form.Item>
                        {packageType === 'extra' ? (
                            <Form.Item
                                label={tF('Assigned Companies')}
                                name="companyIds"
                                rules={[{ required: true, message: tV('Please select customers companies') }]}
                            >
                                <Select
                                    disabled={!currentCompany}
                                    mode="multiple"
                                    showSearch
                                    filterOption={(input, option: any) =>
                                        option?.label.toLowerCase().indexOf(input.toLowerCase()) >= 0 ||
                                        option?.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                    placeholder={tF('Choose customers companies')}
                                    className={TariffsStyles.width100}
                                    options={childrenCompanySelect}
                                    value={currentChildrenCompanies}
                                    onChange={(value) => {
                                        setChildrenCompanies(value);
                                        form.setFieldValue('companyIds', value);
                                    }}
                                />
                            </Form.Item>
                        ) : null}
                        {packageType === 'extra' ? (
                            <Form.Item
                                label={tF('Price')}
                                name="price"
                                rules={[{ required: true, message: tV('Please enter price!') }]}
                            >
                                <InputNumber
                                    step={1}
                                    min={0}
                                    precision={2}
                                    addonBefore={currencyObj?.currencyCode || 'EUR'}
                                    className={TariffsStyles.inputPrice}
                                />
                            </Form.Item>
                        ) : null}

                        {packageType === 'extra' ? (
                            <div className={TariffsStyles.formRowItems}>
                                <Form.Item label={tF('Product ID')} name="productId" className={TariffsStyles.flex1}>
                                    <Input placeholder={tT('e.g. prod_123')} />
                                </Form.Item>
                                <Form.Item label={tF('Plan Type')} name="planType" className={TariffsStyles.flex1}>
                                    <Input placeholder={tT('e.g. monthly')} />
                                </Form.Item>
                            </div>
                        ) : null}

                        <Divider />

                        <div className={TariffsStyles.sectionHeader}>
                            <DatabaseFilled />
                            <h3 className={TariffsStyles.sectionTitle}>{tF('Allowances')}</h3>
                        </div>

                        <div className={TariffsStyles.allowancesContainer}>
                            <div
                                className={cx(
                                    TariffsStyles.allowanceGroup,
                                    dataToggle
                                        ? TariffsStyles.allowanceGroupActive
                                        : TariffsStyles.allowanceGroupPassive,
                                )}
                            >
                                <div className={TariffsStyles.switchWrapper}>
                                    <label className={TariffsStyles.label}>{tF('Data')}</label>
                                    <Switch checked={dataToggle} onChange={setDataToggle} />
                                </div>
                                {dataToggle && (
                                    <div className={TariffsStyles.inputWrapper}>
                                        <Form.Item
                                            name="dataAllowance"
                                            rules={[{ required: true, message: tV('Please enter data allowance!') }]}
                                        >
                                            <InputNumber
                                                min={0.1}
                                                step={1}
                                                placeholder="10.0"
                                                stringMode={false}
                                                parser={(value) => {
                                                    const num = parseFloat(value || '0');
                                                    return Math.max(0.1, num);
                                                }}
                                                addonAfter={
                                                    <LimitUnitSelector
                                                        disabled={false}
                                                        unitFieldName={'dataAllowanceUnits'}
                                                    />
                                                }
                                            />
                                        </Form.Item>
                                    </div>
                                )}
                            </div>
                            <div
                                className={cx(
                                    TariffsStyles.allowanceGroup,
                                    callsToggle
                                        ? TariffsStyles.allowanceGroupActive
                                        : TariffsStyles.allowanceGroupPassive,
                                )}
                            >
                                <div className={TariffsStyles.switchWrapper}>
                                    <label className={TariffsStyles.label}>{tF('Voice')}</label>
                                    <Switch checked={callsToggle} onChange={setCallsToggle} />
                                </div>
                                {callsToggle && (
                                    <div className={TariffsStyles.inputWrapper}>
                                        <Form.Item
                                            name="callsAllowance"
                                            rules={[
                                                { required: true, message: tV('Please enter the call allowance!') },
                                            ]}
                                        >
                                            <InputNumber
                                                min={1}
                                                step={1}
                                                placeholder="10"
                                                stringMode={false}
                                                parser={(value) => {
                                                    const num = parseInt(value || '0', 10);
                                                    return Math.max(1, num);
                                                }}
                                                addonAfter={
                                                    <FormSelect
                                                        defaultValue={'hours'}
                                                        fieldName={'callsAllowanceUnits'}
                                                        isNotClear={true}
                                                        isAddonAfterLong={true}
                                                        rules={apiMessagesGetFormRules}
                                                        useSelectHook={useTime}
                                                    />
                                                }
                                            />
                                        </Form.Item>
                                    </div>
                                )}
                            </div>

                            <div
                                className={cx(
                                    TariffsStyles.allowanceGroup,
                                    smsToggle
                                        ? TariffsStyles.allowanceGroupActive
                                        : TariffsStyles.allowanceGroupPassive,
                                )}
                            >
                                <div className={TariffsStyles.switchWrapper}>
                                    <label className={TariffsStyles.label}>{tF('SMS')}</label>
                                    <Switch checked={smsToggle} onChange={setSmsToggle} />
                                </div>
                                {smsToggle && (
                                    <div className={TariffsStyles.inputWrapper}>
                                        <Form.Item
                                            name="smsAllowance"
                                            rules={[{ required: true, message: tV('Please enter the SMS allowance!') }]}
                                        >
                                            <InputNumber
                                                min={1}
                                                step={1}
                                                placeholder="1000"
                                                stringMode={false}
                                                parser={(value) => {
                                                    const num = parseFloat(value || '0');
                                                    return Math.max(1, num);
                                                }}
                                            />
                                        </Form.Item>
                                    </div>
                                )}
                            </div>
                        </div>

                        <Form.Item name="allowanceValidation" noStyle className={TariffsStyles.hidden}>
                            <Input type="hidden" />
                        </Form.Item>
                        {!dataToggle && !callsToggle && !smsToggle && (
                            <div className={TariffsStyles.alert}>{tV('At least one allowance must be enabled!')}</div>
                        )}

                        <Divider />

                        <div>
                            <div className={TariffsStyles.sectionHeader}>
                                <GlobalOutlined />
                                <h3 className={TariffsStyles.operatorsTitle}>{tT('Coverage and Selection')}</h3>
                            </div>
                            {currentCompany || tariffCompanyId ? (
                                <>
                                    <div className={TariffsStyles.selectorContainer}>
                                        <SelectableList
                                            pagination={false}
                                            title={tF('Operators')}
                                            selectedItems={selectedOperators}
                                            setSelected={setSelectedOperators}
                                            filter={{
                                                cardsSubsCompanyId:
                                                    currentCompany?.toString() || tariffCompanyId?.toString() || '',
                                            }}
                                            fetchFunction={(params: IParams) =>
                                                getOperatorsSelect(false, false, [], [], params)
                                            }
                                        />
                                    </div>
                                    <div className={TariffsStyles.selectorContainer}>
                                        <SelectableList
                                            title={tF('Networks')}
                                            selectedItems={selectedNetworks}
                                            setSelected={setSelectedNetworks}
                                            fetchFunction={(params: IParams) => getNetworksSelect(params, true)}
                                            isNetworks={true}
                                        />
                                    </div>
                                </>
                            ) : (
                                <div className={TariffsStyles.selectorContainer}>
                                    {tV('Please select an owner first')}
                                </div>
                            )}
                        </div>
                    </Form>

                    <div className={TariffsStyles.formActions}>
                        <Button onClick={onBack}>{tB('Cancel')}</Button>
                        <Button
                            type="primary"
                            onClick={handleSave}
                            disabled={selectedOperators.length === 0 || selectedNetworks.length === 0}
                        >
                            {tB('Confirm')}
                        </Button>
                    </div>
                </div>
            </div>
        );
    },
);
